import { db } from '@/js/external-js/config/main_config';
import { waitForCompanyID } from '@/js/external-js/variableModules/utils/waitForCompanyID.util';
import { Timestamp, serverTimestamp, FieldValue, DocumentData, doc, getDoc, setDoc, updateDoc, deleteField } from 'firebase/firestore';

// Interface for User Role data in Firestore (with FieldValue for server timestamps)
interface UserRoleData {
  userId: string;
  teamName: string;
  ismsRole: string;
  ismsCommittee: string;
  dateAdded?: Date | Timestamp | FieldValue | { toDate: () => Date };
  dateUpdated?: Date | Timestamp | FieldValue | { toDate: () => Date };
  addedBy?: string;
  updatedBy?: string;
}

// Interface for ISMS Role data
interface ISMSRoleData {
  roleName: string;
  roleDescription: string;
  dateAdded?: Date | Timestamp | FieldValue | { toDate: () => Date };
  dateUpdated?: Date | Timestamp | FieldValue | { toDate: () => Date };
  addedBy?: string;
  updatedBy?: string;
}

// Interface for the processed User Role
export interface UserRole extends Omit<UserRoleData, 'dateAdded' | 'dateUpdated'> {
  id: string;
  dateAdded: Date;
  dateUpdated: Date;
}

// Interface for the processed ISMS Role
export interface ISMSRole extends Omit<ISMSRoleData, 'dateAdded' | 'dateUpdated'> {
  id: string;
  dateAdded: Date;
  dateUpdated: Date;
}

export interface CurrentUsers {
  businessUnit: string;
  dateAdded: {
    __time__: string;
  };
  ismsCommittee: string;
  ismsRole: string;
  lastUpdated: FieldValue;
  lastUpdatedBy: string;
  licensesUsed: string;
  role: string;
  teamName: string;
  userID: string;
  userNumber: string;
}

/**
 * Fetches all user roles from Firestore
 */
export async function getAllUserRoles(): Promise<UserRole[]> {
  try {
    const companyID = await waitForCompanyID();
    const docPath = `/customers/${companyID}`;
    const docRef = doc(db, docPath);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      console.warn('Customer document does not exist');
      return [];
    }

    const data = docSnap.data() as DocumentData & {
      customerOrg?: {
        users?: Record<string, Omit<UserRoleData, 'userId'>>
      }
    };

    if (!data?.customerOrg?.users) {
      console.warn('No users data found');
      return [];
    }

    const usersData = data.customerOrg.users;
    const userRoles: UserRole[] = [];

    for (const [id, userData] of Object.entries(usersData)) {
      const userId = userData?.userID ? userData.userID : id;
      
      const userRole: UserRole = {
        id: userId,
        userId,
        teamName: userData.teamName || 'N/A',
        ismsRole: userData.ismsRole || 'N/A',
        ismsCommittee: userData.ismsCommittee || 'N/A',
        dateAdded: new Date(), // Default value, will be updated if available
        dateUpdated: new Date(), // Default value, will be updated if available
      };

      // Convert Firestore timestamps to Date objects if they exist
      if (userData.dateAdded) {
        userRole.dateAdded = typeof userData.dateAdded === 'object' && userData.dateAdded !== null && 'toDate' in userData.dateAdded
          ? (userData.dateAdded as { toDate: () => Date }).toDate()
          : userData.dateAdded instanceof Date
            ? userData.dateAdded
            : new Date();
      }
      if (userData.dateUpdated) {
        userRole.dateUpdated = typeof userData.dateUpdated === 'object' && userData.dateUpdated !== null && 'toDate' in userData.dateUpdated
          ? (userData.dateUpdated as { toDate: () => Date }).toDate()
          : userData.dateUpdated instanceof Date
            ? userData.dateUpdated
            : new Date();
      }

      userRoles.push(userRole);
    }

    return userRoles;
  } catch (error) {
    console.error('Error fetching user roles:', error);
    throw new Error('Failed to load user roles');
  }
}

/**
 * Fetches all ISMS roles from Firestore
 */
export async function getISMSRoles(): Promise<{
  ismsRoles: ISMSRole[];
  ismsCommitteeRoles: ISMSRole[];
}> {
  try {
    const companyID = await waitForCompanyID();
    const docPath = `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`;
    const docRef = doc(db, docPath);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      console.warn('ISMS Manual document does not exist');
      return { ismsRoles: [], ismsCommitteeRoles: [] };
    }

    const data = docSnap.data() as DocumentData & {
      ismsRoles?: Record<string, Omit<ISMSRoleData, 'id'>>;
      ismsCommittee?: Record<string, Omit<ISMSRoleData, 'id'>>;
    };

    const processRoles = (roles: Record<string, Omit<ISMSRoleData, 'id'>> | undefined): ISMSRole[] => {
      if (!roles) return [];
      
      return Object.entries(roles).map(([id, role]) => {
        const parseDate = (dateValue: any): Date => {
          if (!dateValue) return new Date();
          if (dateValue instanceof Date) return dateValue;
          if (typeof dateValue === 'object' && dateValue !== null && 'toDate' in dateValue) {
            return (dateValue as { toDate: () => Date }).toDate();
          }
          return new Date();
        };
        
        return {
          id,
          roleName: role.roleName,
          roleDescription: role.roleDescription,
          dateAdded: parseDate(role.dateAdded),
          dateUpdated: parseDate(role.dateUpdated),
        };
      });
    };

    return {
      ismsRoles: processRoles(data.ismsRoles),
      ismsCommitteeRoles: processRoles(data.ismsCommittee),
    };
  } catch (error) {
    console.error('Error fetching ISMS roles:', error);
    throw new Error('Failed to load ISMS roles');
  }
}

/**
 * Updates a user's role in Firestore
 */
export async function updateUserRole(
  userId: string,
  updates: Partial<Omit<UserRoleData, 'userId' | 'dateAdded' | 'addedBy'>>
): Promise<void> {
  try {
    const companyID = await waitForCompanyID();
    const docPath = `/customers/${companyID}`;
    const docRef = doc(db, docPath);
    
    // Get current user data to preserve existing fields
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      throw new Error('Customer document does not exist');
    }

    const customerData = docSnap.data();
    const currentUserData = customerData.customerOrg?.users?.[userId];
    
    if (!currentUserData) {
      throw new Error('User not found');
    }

    // Prepare update data
    const updateData: Record<string, any> = {
      [`customerOrg.users.${userId}`]: {
        ...currentUserData,
        ...updates,
        dateUpdated: serverTimestamp(),
      },
    };

    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error('Error updating user role:', error);
    throw new Error('Failed to update user role');
  }
}

/**
 * Sets up a real-time listener for user roles changes
 */
export function onUserRolesChange(
  callback: (roles: UserRole[]) => void
): () => void {
  // Implementation would use onSnapshot from Firestore
  // This is a simplified version that polls for changes
  let isSubscribed = true;
  let lastRoles: UserRole[] = [];

  const checkForUpdates = async () => {
    if (!isSubscribed) return;
    
    try {
      const roles = await getAllUserRoles();
      
      // Only call callback if roles have changed
      if (JSON.stringify(roles) !== JSON.stringify(lastRoles)) {
        lastRoles = roles;
        callback(roles);
      }
    } catch (error) {
      console.error('Error in roles listener:', error);
    }
    
    // Poll every 10 seconds
    if (isSubscribed) {
      setTimeout(checkForUpdates, 10000);
    }
  };

  // Initial check
  checkForUpdates();

  // Return unsubscribe function
  return () => {
    isSubscribed = false;
  };
}

// Export all functions
export default {
  getAllUserRoles,
  getISMSRoles,
  updateUserRole,
  onUserRolesChange,
};
